import React from 'react';

import useAuthenticatedSession from '~/hooks/useAuthenticatedSession';

import {
	IsConductorClient,
} from '~/config';

import {
	_refreshToken,
	activateSessionRefresh,
	deactivateSessionRefresh,
} from '~/utilities/sessionRefreshUtil';



const AuthenticatedSession: React.FC = () => {
	// Initialize as true for Conductor clients to skip query on first render
	// This ensures refresh happens before query runs (prevents race condition)
	const [isRefreshingOnMount, setIsRefreshingOnMount] = React.useState(
		IsConductorClient,
	);
	// Use ref instead of state to prevent effect re-run loop
	const hasAttemptedRefreshRef = React.useRef(false);

	// Attempt token refresh on mount (before GraphQL query) for Conductor clients
	// This handles the case where user closed tab and reopened after token expiration
	React.useEffect(
		() => {
			if (!IsConductorClient || hasAttemptedRefreshRef.current) {
				return;
			}

			hasAttemptedRefreshRef.current = true;

			let isMounted = true;

			// Add timeout fallback to ensure query runs even if refresh hangs
			const timeoutId = setTimeout(
				() => {
					if (isMounted) {
						setIsRefreshingOnMount(false);
					}
				},
				10_000, // 10 second timeout
			);

			_refreshToken()
				.finally(() => {
					clearTimeout(timeoutId);
					if (isMounted) {
						setIsRefreshingOnMount(false);
					}
				});

			return () => {
				isMounted = false;
				clearTimeout(timeoutId);
			};
		},
		[], // Empty dependency array - run once on mount
	);

	// Skip GraphQL query until refresh attempt completes (for Conductor clients)
	const shouldSkipQuery = IsConductorClient && isRefreshingOnMount;

	// Use hook's query result instead of calling query directly (avoids duplicate queries)
	const { isAuthenticated } = useAuthenticatedSession({
		skip: shouldSkipQuery,
	});

	React.useEffect(
		() => {
			if (!IsConductorClient || !isAuthenticated) {
				return;
			}

			activateSessionRefresh(true);

			return () => {
				deactivateSessionRefresh();
			};
		},
		[
			isAuthenticated,
		],
	);

	return null;
};



export default AuthenticatedSession;
